import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import QRCode from "qrcode";
import { portalAPI, imgUrl } from "../../api";
import { formatPrecio, getLabelTipo, AMENIDADES } from "../../utils";
import { useSettings } from "../../context/SettingsContext";
import "./FichaPropiedad.css";

// Mismo orden que la galería de la web: portada primero, luego como se acomodó en el admin
const ordenarFotos = (imgs = []) =>
    [...imgs].sort((a, b) =>
        (b.is_cover === true) - (a.is_cover === true) ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.id).localeCompare(String(b.id))
    );

// Espera a que todas las fotos terminen de cargar (o fallen), con un límite de tiempo
const esperarImagenes = (contenedor, maxMs = 25000) => {
  const imgs = [...contenedor.querySelectorAll("img")];
  const cargas = imgs.map((img) =>
      img.complete
          ? Promise.resolve()
          : new Promise((ok) => {
            img.addEventListener("load", ok, { once: true });
            img.addEventListener("error", ok, { once: true });
          })
  );
  return Promise.race([Promise.all(cargas), new Promise((ok) => setTimeout(ok, maxMs))]);
};

// "5212711234567" → "(271) 123-4567"
const formatoTel = (valor = "") => {
  const d = valor.replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : valor;
};

export default function FichaPropiedad() {
  const { slug } = useParams();
  const { settings, cargando: cargandoSettings } = useSettings();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [qrWeb, setQrWeb] = useState(null);
  const [qrMapa, setQrMapa] = useState(null);
  const [lista, setLista] = useState(false);
  const hojaRef = useRef(null);
  const yaImprimio = useRef(false);

  const urlWeb = `${window.location.origin}/propiedades/${slug}`;
  const tieneMapa = Boolean(p?.latitude && p?.longitude);

  useEffect(() => {
    portalAPI.detalle(slug)
        .then((r) => setP(r.data.data))
        .catch(() => setError(true));
  }, [slug]);

  // Códigos QR y nombre del archivo PDF
  useEffect(() => {
    if (!p) return;
    const opciones = { margin: 1, width: 300, color: { dark: "#1A1A1A", light: "#FFFFFF" } };
    QRCode.toDataURL(urlWeb, opciones).then(setQrWeb).catch(() => setQrWeb(""));
    if (p.latitude && p.longitude) {
      QRCode.toDataURL(`https://www.google.com/maps?q=${p.latitude},${p.longitude}`, opciones)
          .then(setQrMapa).catch(() => setQrMapa(""));
    }
    // El navegador usa el título de la página como nombre sugerido del PDF
    document.title = `Ficha ${p.custom_id ? `${p.custom_id} - ` : ""}${p.title}`;
  }, [p, urlWeb]);

  // Cuando todo cargó (fotos, QR, configuración), abre el diálogo una sola vez
  const qrsListos = qrWeb !== null && (!tieneMapa || qrMapa !== null);
  useEffect(() => {
    if (!p || !qrsListos || cargandoSettings || !hojaRef.current) return;
    let cancelado = false;
    esperarImagenes(hojaRef.current).then(() => {
      if (cancelado) return;
      setLista(true);
      if (!yaImprimio.current) {
        yaImprimio.current = true;
        setTimeout(() => window.print(), 400);
      }
    });
    return () => { cancelado = true; };
  }, [p, qrsListos, cargandoSettings]);

  if (error) {
    return (
        <div className="fi-estado">
          <h2>No encontramos esta propiedad</h2>
          <p>Es posible que ya no esté disponible.</p>
          <Link to="/propiedades" className="btn btn-primary">Ver propiedades</Link>
        </div>
    );
  }

  if (!p) {
    return (
        <div className="fi-estado">
          <div className="spinner" />
          <p>Preparando la ficha técnica…</p>
        </div>
    );
  }

  const fotos = ordenarFotos(p.images || []);
  const principales = fotos.slice(0, 5);
  const restantes = fotos.slice(5);
  const total = fotos.length;

  const servicios = [
    { label: "Cisterna", val: p.has_cistern, icon: "💧" },
    { label: "Tinaco", val: p.has_water_tank, icon: "🪣" },
    { label: "Gas natural", val: p.has_natural_gas, icon: "🔥" },
    { label: "Electricidad", val: p.has_electricity, icon: "⚡" },
    { label: "Agua", val: p.has_water, icon: "🚰" },
    { label: "Drenaje", val: p.has_drainage, icon: "🪠" },
    { label: "Internet", val: p.has_internet, icon: "📡" },
  ].filter((s) => s.val);

  const caracteristicas = [
    p.bedrooms != null && { icon: "🛏", val: p.bedrooms, label: "Recámaras" },
    p.bathrooms != null && { icon: "🚿", val: p.bathrooms, label: "Baños" },
    p.half_bathrooms > 0 && { icon: "🚽", val: p.half_bathrooms, label: "Medios baños" },
    p.parking_spaces != null && { icon: "🚗", val: p.parking_spaces, label: "Estacionamiento" },
    p.construction_m2 != null && { icon: "📐", val: `${p.construction_m2} m²`, label: "Construcción" },
    p.land_m2 != null && { icon: "🌿", val: `${p.land_m2} m²`, label: "Terreno" },
    p.floors != null && { icon: "🏢", val: p.floors, label: "Pisos" },
    p.year_built != null && { icon: "📅", val: p.year_built, label: "Año" },
    p.furnished && {
      icon: "🛋️",
      val: p.furnished === "AMUEBLADO" ? "Sí" : p.furnished === "SEMI_AMUEBLADO" ? "Semi" : "No",
      label: "Amueblado",
    },
  ].filter(Boolean);

  const ubicacion = `${p.address ? `${p.address}, ` : ""}${p.neighborhood ? `${p.neighborhood}, ` : ""}${p.city}${p.state ? `, ${p.state}` : ""}`;
  const telefono = settings.contact_phone;
  const whatsapp = settings.whatsapp_number;
  const mostrarWhats = whatsapp && formatoTel(whatsapp) !== formatoTel(telefono || "");
  const fecha = new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });

  return (
      <div className="fi-pagina">
        {/* Barra de herramientas (no se imprime) */}
        <div className="fi-barra fi-no-imprimir">
          <div>
            <strong>Ficha técnica</strong>
            <span className="fi-barra-estado">
            {lista ? "Lista para guardar" : "Cargando fotos…"}
          </span>
          </div>
          <p className="fi-barra-ayuda">
            En el diálogo de impresión, elige <b>"Guardar como PDF"</b> como destino.
          </p>
          <div className="fi-barra-botones">
            <Link to={`/propiedades/${slug}`} className="btn btn-ghost btn-sm">← Volver</Link>
            <button type="button" className="btn btn-primary btn-sm" disabled={!lista} onClick={() => window.print()}>
              🖨️ Guardar como PDF
            </button>
          </div>
        </div>

        <article className="ficha" ref={hojaRef}>
          {/* ── Encabezado con la marca ── */}
          <header className="fi-encabezado">
            <div className="fi-marca">
              {/* Con logo, solo el logo (ya trae el nombre); sin logo, el nombre del sitio */}
              {settings.logo_url
                  ? <img src={imgUrl(settings.logo_url)} alt={settings.site_name} className="fi-logo" />
                  : <span className="fi-sitio">{settings.site_name}</span>}
            </div>
            <div className="fi-contacto">
              {telefono && <span>📞 {telefono}</span>}
              {mostrarWhats && <span>💬 {formatoTel(whatsapp)}</span>}
              {settings.contact_email && <span>✉️ {settings.contact_email}</span>}
              <span>🌐 {window.location.host}</span>
            </div>
          </header>

          {/* ── Título y precio ── */}
          <section className="fi-titulo-fila">
            <div className="fi-titulo-info">
              <div className="fi-badges">
              <span className={p.operation === "VENTA" ? "badge-venta" : "badge-renta"}>
                {p.operation === "VENTA" ? "Venta" : "Renta"}
              </span>
                <span className="badge-tipo">{getLabelTipo(p.type)}</span>
              </div>
              <h1 className="fi-titulo">{p.title}</h1>
              <p className="fi-ubicacion">📍 {ubicacion}</p>
              {p.custom_id && <p className="fi-clave">Clave: <b>{p.custom_id}</b></p>}
            </div>
            <div className="fi-precio-caja">
              <p className="fi-precio-etiqueta">{p.operation === "VENTA" ? "En Venta" : "En Renta"}</p>
              <p className="fi-precio">{formatPrecio(p.price, p.currency, p.show_price)}</p>
              {p.price_negotiable && p.show_price && <span className="badge-featured">💬 Precio a tratar</span>}
            </div>
          </section>

          {/* ── Galería principal (mismo acomodo que la web) ── */}
          {principales.length > 0 && (
              <section className={`fi-galeria fi-n${principales.length}`}>
                {principales.map((img, i) => (
                    <figure key={img.id ?? i} className={`fi-celda fi-celda-${i}`}>
                      <img src={imgUrl(img.url_medium || img.url)} alt="" />
                    </figure>
                ))}
              </section>
          )}

          {/* ── Características ── */}
          {caracteristicas.length > 0 && (
              <section className="fi-seccion">
                <h2 className="fi-seccion-titulo">Características</h2>
                <div className="fi-caract-grid">
                  {caracteristicas.map((c) => (
                      <div key={c.label} className="fi-caract">
                        <span className="fi-caract-icono">{c.icon}</span>
                        <span className="fi-caract-val">{c.val}</span>
                        <span className="fi-caract-label">{c.label}</span>
                      </div>
                  ))}
                </div>
              </section>
          )}

          {/* ── Descripción (en la primera hoja: es lo segundo que más se lee) ── */}
          {p.description && (
              <section className="fi-seccion fi-seccion-desc">
                <h2 className="fi-seccion-titulo">Descripción</h2>
                <div className="fi-descripcion" dangerouslySetInnerHTML={{ __html: p.description }} />
              </section>
          )}

          {/* ── Galería completa: todas las fotos restantes, grandes ── */}
          {restantes.length > 0 && (
              <section className="fi-galeria-completa">
                <h2 className="fi-seccion-titulo">Galería completa · {total} fotos</h2>
                <div className="fi-fotos-grid">
                  {restantes.map((img, i) => (
                      <figure key={img.id ?? i} className="fi-foto">
                        <img src={imgUrl(img.url_medium || img.url)} alt="" />
                        <figcaption>{i + 6} / {total}</figcaption>
                      </figure>
                  ))}
                </div>
              </section>
          )}

          {/* ── Información detallada ── */}
          <section className="fi-detalles">
            {servicios.length > 0 && (
                <div className="fi-seccion">
                  <h2 className="fi-seccion-titulo">Servicios</h2>
                  <div className="fi-chips">
                    {servicios.map((s) => (
                        <span key={s.label} className="fi-chip fi-chip-ok">{s.icon} {s.label}</span>
                    ))}
                  </div>
                </div>
            )}

            {p.amenities?.length > 0 && (
                <div className="fi-seccion">
                  <h2 className="fi-seccion-titulo">Amenidades</h2>
                  <div className="fi-chips">
                    {p.amenities.map((a) => {
                      const info = AMENIDADES.find((x) => x.value === a);
                      return <span key={a} className="fi-chip">{info?.emoji || "✓"} {info?.label || a}</span>;
                    })}
                  </div>
                </div>
            )}

            {/* Ubicación y códigos QR */}
            <div className="fi-seccion">
              <h2 className="fi-seccion-titulo">Ubicación y más información</h2>
              <div className="fi-qr-fila">
                {tieneMapa && qrMapa && (
                    <div className="fi-qr">
                      <img src={qrMapa} alt="" />
                      <div>
                        <b>Ver ubicación</b>
                        <span>Escanea para abrir en Google Maps</span>
                        <span className="fi-qr-dato">📍 {ubicacion}</span>
                      </div>
                    </div>
                )}
                {qrWeb && (
                    <div className="fi-qr">
                      <img src={qrWeb} alt="" />
                      <div>
                        <b>Ver en línea</b>
                        <span>Escanea para ver todas las fotos y contactarnos</span>
                        <span className="fi-qr-dato">{urlWeb.replace(/^https?:\/\//, "")}</span>
                      </div>
                    </div>
                )}
              </div>
            </div>

            <footer className="fi-pie">
              <span>{settings.site_name}{telefono ? ` · ${telefono}` : ""}</span>
              <span>Ficha generada el {fecha}. Precio y disponibilidad sujetos a cambio sin previo aviso.</span>
            </footer>
          </section>
        </article>
      </div>
  );
}